import { kvGet, kvSet, type TruckRow, type WorkOrderPriority, type WorkOrderRow, type WorkOrderStatus } from '@routeback/core';
import { Modal, Segmented, useToast } from '@routeback/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../lib/app';
import { shopLabel, useMyShop } from '../../lib/shop';
import { supabase } from '../../lib/supabase';
import { NewJobForm } from '../maintdash/forms';
import { PRIORITY_LABEL, WO_STATUS_LABEL, woLabel } from '../maintdash/workorders';
import { formatRange } from '../timeoff/timeoff';
import { terminalKey } from '../manage/terminalNumber';

/** One row of my_shop_work_orders: a flagged job at any company this mechanic works for. */
export interface ShopJob {
  company_id: string; company_name: string; terminal_id: string | null; terminal_name: string | null; terminal_code: string;
  work_order_id: string; number: number | null; unit_number: string | null; title: string; description: string;
  status: WorkOrderStatus; priority: WorkOrderPriority; opened_on: string; completed_on: string | null; updated_at: string; assigned_to_me: boolean;
  /** The shop's tech on it (0042) — the shop owner picks. */
  shop_tech_id?: string | null; shop_tech_name?: string | null;
}

/**
 * The terminal filter's key for a job: its FedEx terminal number when the
 * company entered one (so every company's jobs at that building group
 * together), else that company's own terminal. '' = no terminal.
 */
export function shopTerminalKey(j: Pick<ShopJob, 'terminal_id' | 'terminal_code'>): string {
  const k = terminalKey(j.terminal_code);
  return k ? `n:${k}` : j.terminal_id ? `t:${j.terminal_id}` : '';
}

/** The filter's choices: one per building, "0601 Carol Stream", in number order. */
export function shopTerminals(jobs: ShopJob[], multiCompany: boolean): [string, string][] {
  const m = new Map<string, string>();
  for (const j of jobs) {
    const k = shopTerminalKey(j);
    if (!k || m.has(k)) continue;
    m.set(k, j.terminal_code ? `${j.terminal_code} ${j.terminal_name ?? ''}`.trim() : `${multiCompany ? `${j.company_name} · ` : ''}${j.terminal_name ?? ''}`);
  }
  return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
}

/**
 * Mechanic home: every job flagged "for mechanic" across all the companies
 * this login works for, open jobs first — with company and terminal filters.
 * Online it asks the server (my_shop_work_orders); offline, or in local mode,
 * it shows the jobs this phone already has.
 */
export function ShopView() {
  const app = useApp();
  const nav = useNavigate();
  const toast = useToast();
  const [done, setDone] = useState(false);
  const [company, setCompany] = useState('');
  const [terminal, setTerminal] = useState('');
  const [adding, setAdding] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const { shop } = useMyShop();
  const jobs = useShopJobs(done);

  if (!jobs) return <div className="spinner" />;
  const companies = [...new Map(jobs.map((j) => [j.company_id, j.company_name])).entries()];
  const terminals = shopTerminals(jobs.filter((j) => !company || j.company_id === company), companies.length > 1);
  const hasTechs = !!shop && (shop.role === 'tech' || shop.techs.length > 0);
  const shown = jobs.filter((j) => (!company || j.company_id === company) && (!terminal || shopTerminalKey(j) === terminal) && (!onlyMine || j.assigned_to_me));

  const open = (j: ShopJob) => {
    if (j.company_id === app.company.id) { nav(`/shop/${j.work_order_id}`); return; }
    window.location.hash = `#/shop/${j.work_order_id}`;
    void app.switchCompany(j.company_id);
  };
  const saveNew = async (wo: WorkOrderRow) => {
    // The server flags a mechanic's job and assigns it to them; do the same here so it shows at once.
    await app.repos.workOrders.save({ ...wo, for_mechanic: true, mechanic_id: app.userId });
    setAdding(false);
    toast('Job added');
    nav(`/shop/${wo.id}`);
  };

  return (
    <div>
      <div className="card card-hero">
        <h2 style={{ fontSize: 20, margin: 0 }}>Shop view</h2>
        <div className="small" style={{ opacity: 0.85 }}>{[shop ? shopLabel(shop) : '', companies.length > 1 ? `${companies.length} companies` : app.details.name].filter(Boolean).join(' · ')}</div>
      </div>
      <div className="row row-wrap" style={{ margin: '4px 12px 8px', gap: 8, justifyContent: 'space-between' }}>
        <Segmented<'open' | 'done'> value={done ? 'done' : 'open'} onChange={(v) => setDone(v === 'done')} options={[{ value: 'open', label: 'Open jobs' }, { value: 'done', label: 'Finished' }]} />
        <div className="row" style={{ gap: 6 }}>
          {hasTechs && <button type="button" className={'chip' + (onlyMine ? ' on' : '')} aria-pressed={onlyMine} onClick={() => setOnlyMine(!onlyMine)}>Only mine</button>}
          {app.active && <button type="button" className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>+ New job</button>}
        </div>
      </div>
      {(companies.length > 1 || terminals.length > 1) && (
        <div className="row row-wrap" style={{ gap: 6, margin: '0 12px 8px' }}>
          {companies.length > 1 && (
            <select aria-label="Company" value={company} onChange={(e) => { setCompany(e.target.value); setTerminal(''); }} style={{ width: 'auto' }}>
              <option value="">All companies</option>
              {companies.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
          {terminals.length > 1 && (
            <select aria-label="Terminal" value={terminal} onChange={(e) => setTerminal(e.target.value)} style={{ width: 'auto' }}>
              <option value="">All terminals</option>
              {terminals.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
        </div>
      )}
      {shown.length === 0 ? (
        <div className="card"><p className="muted" style={{ margin: 0 }}>{done ? 'No finished jobs yet.' : 'No open jobs. When a company sends you a work order it shows up here, with the truck, the driver\'s report and photos.'}</p></div>
      ) : (
        <div className="md-grid">
          {shown.map((j) => (
            <button key={j.work_order_id} type="button" className={`md-card shop-card md-pri-${j.status === 'done' ? 'done' : j.priority}`} onClick={() => open(j)}>
              <div className="md-card-top">
                {j.status !== 'done' && <span className={`md-pri md-pri-${j.priority}`}>{PRIORITY_LABEL[j.priority]}</span>}
                <span className="md-unit">{j.unit_number ?? '—'}</span>
                <span className="muted small">{woLabel(j)}</span>
                {j.status !== 'open' && <span className={'chip' + (j.status === 'done' ? ' on' : '')}>{WO_STATUS_LABEL[j.status]}</span>}
                {j.assigned_to_me && <span className="chip">Assigned to you</span>}
                {!j.assigned_to_me && j.shop_tech_id && <span className="chip">👷 {j.shop_tech_name || 'A tech'}</span>}
              </div>
              <div className="md-title">{j.title || j.description}</div>
              {j.title && j.description && <div className="md-desc">{j.description}</div>}
              <div className="md-meta">
                <span>{[companies.length > 1 ? j.company_name : '', j.terminal_code ? `${j.terminal_code} ${j.terminal_name ?? ''}`.trim() : j.terminal_name ?? '', j.status === 'done' ? `done ${formatRange(j.completed_on, null)}` : `opened ${formatRange(j.opened_on, null)}`].filter(Boolean).join(' · ')}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      <Modal open={adding} onClose={() => setAdding(false)} title={`New job — ${app.details.name}`} closeButton>
        {adding && <NewJobForm people={[]} onSave={(wo) => void saveNew(wo)} onCancel={() => setAdding(false)} />}
      </Modal>
    </div>
  );
}

/** Online: the server's cross-company list (cached). Offline / local mode: this phone's copy for the current company. */
function useShopJobs(includeDone: boolean): ShopJob[] | undefined {
  const app = useApp();
  const key = `shop:${app.userId}:${includeDone ? 'done' : 'open'}`;
  const [remote, setRemote] = useState<ShopJob[] | null>(null);
  const load = useCallback(async () => {
    const cached = await kvGet<ShopJob[]>(key);
    if (cached) setRemote(cached);
    if (!supabase) return;
    const { data, error } = await supabase.rpc('my_shop_work_orders', { include_done: includeDone });
    if (!error && data) {
      const rows = (data as ShopJob[]).filter((j) => (includeDone ? j.status === 'done' || j.status === 'cancelled' : j.status !== 'done' && j.status !== 'cancelled'));
      setRemote(rows);
      await kvSet(key, rows);
    }
  }, [key, includeDone]);
  useEffect(() => { setRemote(null); void load(); }, [load, app.syncStatus.lastSyncAt]);
  const local = useLiveQuery(async () => {
    const t = <T,>(n: string) => app.db.table<T, string>(n).where('company_id').equals(app.company.id).toArray();
    const [wos, trucks] = await Promise.all([t<WorkOrderRow>('work_orders'), t<TruckRow>('trucks')]);
    const tm = new Map(trucks.map((x) => [x.id, x]));
    const isDone = (s: WorkOrderStatus) => s === 'done' || s === 'cancelled';
    return wos
      .filter((w) => !w.deleted_at && w.for_mechanic && isDone(w.status) === includeDone)
      .map<ShopJob>((w) => {
        const tr = tm.get(w.truck_id ?? '');
        const te = app.terminals.find((x) => x.id === tr?.terminal_id);
        return {
          company_id: w.company_id, company_name: app.details.name, terminal_id: tr?.terminal_id ?? null,
          terminal_name: te?.name ?? null, terminal_code: te?.code ?? '', work_order_id: w.id, number: w.number,
          unit_number: tr?.unit_number ?? null, title: w.title, description: w.description, status: w.status, priority: w.priority,
          opened_on: w.opened_on, completed_on: w.completed_on, updated_at: w.updated_at,
          assigned_to_me: w.shop_tech_id ? w.shop_tech_id === app.userId : w.mechanic_id === app.userId, shop_tech_id: w.shop_tech_id ?? null,
        };
      })
      .sort((a, b) => (includeDone ? (b.completed_on ?? '').localeCompare(a.completed_on ?? '') : 0) || ['urgent', 'high', 'normal', 'low'].indexOf(a.priority) - ['urgent', 'high', 'normal', 'low'].indexOf(b.priority) || a.opened_on.localeCompare(b.opened_on));
  }, [app.db, app.company.id, includeDone, app.details.name, app.terminals, app.userId]);
  if (!local) return undefined;
  if (!remote) return local;
  // The server's list, with this phone's newer copies of the current company's jobs (offline edits) on top.
  const mine = new Map(local.map((j) => [j.work_order_id, j]));
  const merged = remote.map((j) => (mine.get(j.work_order_id)?.updated_at ?? '') > j.updated_at ? { ...j, ...mine.get(j.work_order_id)!, company_name: j.company_name, shop_tech_name: mine.get(j.work_order_id)!.shop_tech_id === j.shop_tech_id ? j.shop_tech_name : null } : j);
  for (const j of local) if (!remote.some((r) => r.work_order_id === j.work_order_id)) merged.unshift(j);
  return merged;
}
